<template>
  <v-container>
    <div class="header">
      <h1 class="text-h4">Tiempo de Respuesta de Asesores</h1>
      <DateRangeComponent dense v-model="dateRange" default="Últimos 7 días" />
    </div>

    <div class="kpi-section">
      <CardKPI
        title="Total de Leads"
        :icon="{ icon: 'mdi-account-group' }"
        :value="responseData.overall_stats.total_leads"
      />
      <CardKPI
        title="Tiempo Promedio de Respuesta"
        :icon="{ icon: 'mdi-clock-outline' }"
        :value="formatHours(responseData.overall_stats.average_response_time)"
      />
      <CardKPI
        title="Tiempo Mínimo de Respuesta"
        :icon="{ icon: 'mdi-timer-outline', color: 'green' }"
        :value="formatHours(responseData.overall_stats.min_response_time)"
      />
      <CardKPI
        title="Tiempo Máximo de Respuesta"
        :icon="{ icon: 'mdi-timer-outline', color: 'red' }"
        :value="formatHours(responseData.overall_stats.max_response_time)"
      />
    </div>
    <v-row>
      <v-col cols="12" md="12">
        <v-card>
          <v-card-title
            >Top 10 Asesores con Mejor Tiempo de Respuesta
          </v-card-title>
          <v-card-text>
            <span class="subtitle">Promedio en Horas </span>
            <apexchart
              :options="chartData.options"
              :series="chartData.series"
              type="bar"
              height="350"
            ></apexchart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="my-6">
      <v-card-title>Detalle por Asesor</v-card-title>
      <v-text-field
        class="mx-5"
        v-model="searchTerm"
        label="Buscar por nombre..."
        clearable
        prepend-inner-icon="mdi-magnify"
        bordered
      />
      <v-data-table
        :headers="headers"
        :items="filteredAndSortedBrokers"
        class="elevation-1"
      >
        <template #item.total_leads="{ item }">
          <span
            class="total_leads"
            @click="() => getDetailData(item.broker_id)"
          >
            {{ item.total_leads }}
          </span>
        </template>
        <template #item.average_response_time="{ item }">
          <v-chip :color="getResponseBadgeColor(item.average_response_time)">
            {{ formatHours(item.average_response_time) }}
          </v-chip>
        </template>
        <template #item.min_response_time="{ item }">
          {{ formatHours(item.min_response_time) }}
        </template>
        <template #item.max_response_time="{ item }">
          {{ formatHours(item.max_response_time) }}
        </template>
      </v-data-table>
    </v-card>
    <ModalDetailList
      :items="detailData"
      :loading="loadingDetail"
      :dialog="dialog"
      :onClose="onDialogClose"
    />
  </v-container>
</template>

<script>
import CardKPI from "./component/AvgTimeAttention/CardKPI.vue";
import DateRangeComponent from "./component/dateRange.component.vue";
import {
  fetchDataAvgTimeAttention,
  fetchDataAvgTimeAttentionDetail
} from "../../api/reports-api";
import ModalDetailList from "./component/AvgTimeAttention/ModalDetailList.vue";
import formatResponseTime from "./utils/formatResponteTime";
import colorResponseTime from "./utils/colorResponseTime";

const INITIAL_DATA = {
  brokers: [],
  overall_stats: {
    total_leads: 0,
    average_response_time: 0,
    min_response_time: 0,
    max_response_time: 0
  }
};
export default {
  components: {
    CardKPI,
    DateRangeComponent,
    ModalDetailList
  },
  data() {
    return {
      responseData: INITIAL_DATA,
      searchTerm: "",
      filterLeads: "all",
      sortField: "average_response_time",
      sortDirection: "asc",
      headers: [
        { text: "Asesor", value: "broker_name" },
        { text: "Leads", value: "total_leads" },
        { text: "Tiempo Promedio", value: "average_response_time" },
        { text: "Tiempo Mínimo", value: "min_response_time" },
        { text: "Tiempo Máximo", value: "max_response_time" }
      ],
      dateRange: { start: null, end: null },
      detailData: [],
      loadingDetail: false,
      dialog: false
    };
  },

  watch: {
    dateRange: {
      handler: async function(newVal) {
        this.responseData = await this.fetchData(
          (newVal.start || newVal.end) && newVal
        );
      },
      deep: true
    }
  },
  computed: {
    filteredAndSortedBrokers() {
      let filtered = this.responseData.brokers;
      if (this.searchTerm) {
        filtered = filtered.filter(broker =>
          broker.broker_name
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase())
        );
      }
      return filtered;
    },
    chartData() {
      const topBrokers = [...this.responseData.brokers]
        .sort((a, b) => a.average_response_time - b.average_response_time)
        .slice(0, 10);
      return {
        series: [
          { data: topBrokers.map(broker => broker.average_response_time) }
        ],
        options: {
          chart: { type: "bar" },
          xaxis: {
            categories: topBrokers.map(
              broker => broker.broker_name.split(" ")[0]
            )
          },
          colors: ["#10b981"]
        }
      };
    }
  },
  methods: {
    formatHours: formatResponseTime,
    getResponseBadgeColor: colorResponseTime,
    async fetchData(filter) {
      try {
        const response = await fetchDataAvgTimeAttention({ dateRange: filter });
        return response.data;
      } catch (error) {
        return INITIAL_DATA;
      }
    },
    async fetchDataDetail(filter) {
      try {
        const response = await fetchDataAvgTimeAttentionDetail({ ...filter });
        return response.data;
      } catch (error) {
        return [];
      }
    },
    async getDetailData(brokerId) {
      const filter = {
        dateRange: this.dateRange,
        contactId: brokerId
      };
      this.detailData = [];
      this.dialog = true;
      this.loadingDetail = true;
      this.detailData = await this.fetchDataDetail(filter);
      this.loadingDetail = false;
    },
    onDialogClose() {
      this.dialog = false;
    }
  }
};
</script>

<style scoped>
.kpi-section {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin: 1rem 0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
/* Media queries para md y lg */
@media (min-width: 768px) {
  .kpi-section {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .kpi-section {
    grid-template-columns: repeat(4, 1fr);
  }
}

.subtitle {
  font-size: 0.875rem;
  color: hsl(240 3.8% 46.1%);
}

.total_leads {
  cursor: pointer;
}

.total_leads:hover {
  text-decoration: underline;
}
</style>
